import React, { useState, useEffect, useRef, useCallback } from 'react'
import ListPageHeader from '../common/ListPageHeader.js';
import { useNavigate } from 'react-router-dom'
import { DashboardLayout } from '../common/page_component/index.js'
import Loader from '../common/Loader'
import Alert from '../common/Alert'
import vehicleContractService from '../../services/vehicleContractService'
import { Plus, ScrollText } from 'lucide-react'
import ListFilterPanel from '../common/ListFilterPanel.js'
import Button from '../common/Button.js'
import RowActionsMenu from '../common/RowActionsMenu.js'
import usePaginatedListFetch from '../../utils/usePaginatedListFetch.js'
import { mergeLoadedPage } from '../../utils/mergeLoadedPage.js'
import ListPagination from '../common/ListPagination.js'
import SearchSubmitInput from '../common/SearchSubmitInput.js'
import { useTranslation } from 'react-i18next';
import { listIdLinkClass, listIdLinkMobileClass } from '../common/uiClasses.js';


const VehicleContractList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate()
  const [contracts, setContracts] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [activeDropdown, setActiveDropdown] = useState(null)
  const dropdownRef = useRef(null)
  const perPage = 10

  const fetchContracts = useCallback(async () => {
    try {
      setLoading(true)
      const filters = { page: currentPage, per_page: perPage }
      if (search) filters.search = search
      if (statusFilter) filters.status = statusFilter

      const response = await vehicleContractService.getAll(filters)
      setContracts((prev) => mergeLoadedPage(currentPage, response.data.data || [], prev))
      setTotalPages(response.data.pagination?.pages || 1)
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to load contracts')
      console.error(err)
      if (currentPage <= 1) setContracts([])
    } finally {
      setLoading(false)
    }
  }, [search, statusFilter, currentPage, perPage])

  usePaginatedListFetch({
    fetchPage: fetchContracts,
    setCurrentPage,
    currentPage,
    resetPageDeps: [search, statusFilter],
    debounceWhen: search,
    debounceMs: 0,
  })

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleDelete = async (contractId, contractLabel) => {
    const confirmed = await Alert.confirm(
      `Are you sure you want to delete contract "${contractLabel}"? This cannot be undone.`,
      'Delete Vehicle Contract'
    )
    if (!confirmed) return

    try {
      await vehicleContractService.delete(contractId)
      Alert.success('Contract deleted successfully!')
      if (contracts.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1)
      } else {
        fetchContracts()
      }
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to delete contract')
    }
  }

  const capitalizeStatus = (status) => {
    return status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : ''
  }

  const formatCurrency = (value) => {
    return parseFloat(value || 0).toLocaleString('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const isExpirySoon = (toDate) => {
    if (!toDate) return false
    const today = new Date()
    const expiryDate = new Date(toDate)
    const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24))
    return daysUntilExpiry >= 0 && daysUntilExpiry <= 5
  }

  const isExpired = (toDate) => {
    if (!toDate) return false
    const today = new Date()
    const expiryDate = new Date(toDate)
    return expiryDate < today
  }

  const getStatusBadge = (contract) => {
    if (isExpired(contract.to_date)) {
      return { label: 'Expired', className: 'bg-red-100 text-red-700 border-red-200' }
    }
    if (isExpirySoon(contract.to_date)) {
      return { label: 'Expiring Soon', className: 'bg-yellow-100 text-yellow-700 border-yellow-200' }
    }
    if (contract.status === 'active') {
      return { label: capitalizeStatus(contract.status), className: 'bg-green-100 text-green-700 border-green-200' }
    }
    if (contract.status === 'inactive') {
      return { label: capitalizeStatus(contract.status), className: 'bg-gray-100 text-gray-700 border-gray-200' }
    }
    return { label: capitalizeStatus(contract.status), className: 'bg-blue-100 text-blue-700 border-blue-200' }
  }

  const clearFilters = () => {
    setSearch('')
    setStatusFilter('')
    setCurrentPage(1)
  }

  const hasActiveFilters = search || statusFilter

  return (
    <DashboardLayout>
      <ListPageHeader
        title="Vehicle Contracts"
        icon={ScrollText}
        className="mb-4 sm:mb-6"
      >
        <Button mobileIconOnly variant="primary" icon={Plus} onClick={() => navigate('/vehicle-contracts/add')}>
          Add Contract
        </Button>
      </ListPageHeader>

      <ListFilterPanel hasActiveFilters={hasActiveFilters} onClearFilters={clearFilters}>
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">{t('common.search')}</label>
          <SearchSubmitInput
            value={search}
            onSearch={setSearch}
            placeholder="Contract ID, party name..."
            inputClassName="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
          />
        </div>
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="completed">Completed</option>
          </select>
        </div>
      </ListFilterPanel>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        {loading && currentPage === 1 ? (
          <div className="flex items-center justify-center py-20">
            <Loader fullScreen={false} size="default" message="Loading contracts..." />
          </div>
        ) : contracts.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">📋</div>
            <p className="text-gray-600 text-lg font-semibold">No contracts found</p>
            <p className="text-gray-500 mt-2">Try adjusting your filters or create a new contract</p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden xl:block w-full overflow-x-auto">
              <table className="w-full min-w-[1000px] border-collapse">
                <thead className="bg-gray-50 rounded-t-xl">
                  <tr className="border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Contract ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap min-w-[140px]">Party / Vehicle</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Duration</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Paid</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {contracts.map((contract, index) => {
                    const statusBadge = getStatusBadge(contract)
                    return (
                      <tr
                        key={contract.id}
                        className={`hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                      >
                        <td className="px-5 py-3 text-sm whitespace-nowrap">
                          <span
                            onClick={() => navigate(`/vehicle-contracts/view/${contract.id}`)}
                            className={listIdLinkClass}
                          >
                            {contract.contract_id}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-sm min-w-[140px]">
                          <div className="font-regular text-gray-900 break-words">{contract.contract_party_name}</div>
                          <div className="text-xs text-gray-400 mt-0.5">{contract.vehicle_number}</div>
                        </td>
                        <td className="px-5 py-3 text-sm whitespace-nowrap text-gray-900">
                          <div>{formatDate(contract.from_date)}</div>
                          <div className="text-xs text-gray-400">to {formatDate(contract.to_date)}</div>
                        </td>
                        <td className="px-5 py-3 text-sm whitespace-nowrap font-regular text-gray-900">
                          {formatCurrency(contract.total_contracted_cost)}
                        </td>
                        <td className="px-5 py-3 text-sm whitespace-nowrap font-regular text-green-600">
                          {formatCurrency(contract.paid_amount)}
                        </td>
                        <td className="px-5 py-3 text-sm whitespace-nowrap">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${statusBadge.className}`}>
                            {statusBadge.label}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-sm text-center">
                          <RowActionsMenu
                            isOpen={activeDropdown === contract.id}
                            onToggle={() => setActiveDropdown(activeDropdown === contract.id ? null : contract.id)}
                            onClose={() => setActiveDropdown(null)}
                            menuRef={dropdownRef}
                            onView={() => navigate(`/vehicle-contracts/view/${contract.id}`)}
                            onEdit={() => navigate(`/vehicle-contracts/edit/${contract.id}`)}
                            onDelete={() => handleDelete(contract.id, contract.contract_id)}
                          />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Tablet & Mobile cards */}
            <div className="xl:hidden space-y-3 p-4">
              {contracts.map((contract) => {
                const statusBadge = getStatusBadge(contract)
                return (
                  <div key={`card-${contract.id}`} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 border-l-4 border-l-blue-500">
                    <div className="flex justify-between items-start mb-3 gap-2">
                      <div className="min-w-0">
                        <span
                          onClick={() => navigate(`/vehicle-contracts/view/${contract.id}`)}
                          className={listIdLinkMobileClass}
                        >
                          #{contract.contract_id}
                        </span>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {formatDate(contract.from_date)} – {formatDate(contract.to_date)}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${statusBadge.className}`}>
                          {statusBadge.label}
                        </span>
                        <RowActionsMenu
                          isOpen={activeDropdown === contract.id}
                          onToggle={() => setActiveDropdown(activeDropdown === contract.id ? null : contract.id)}
                          onClose={() => setActiveDropdown(null)}
                          menuRef={dropdownRef}
                          onView={() => navigate(`/vehicle-contracts/view/${contract.id}`)}
                          onEdit={() => navigate(`/vehicle-contracts/edit/${contract.id}`)}
                          onDelete={() => handleDelete(contract.id, contract.contract_id)}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                      <div className="min-w-0">
                        <p className="text-xs text-gray-500 font-medium">Party</p>
                        <p className="font-semibold text-gray-800 break-words">{contract.contract_party_name}</p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-gray-500 font-medium">Vehicle</p>
                        <p className="font-semibold text-gray-800">{contract.vehicle_number}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Total Amount</p>
                        <p className="font-bold text-gray-900">{formatCurrency(contract.total_contracted_cost)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Paid</p>
                        <p className="font-bold text-green-600">{formatCurrency(contract.paid_amount)}</p>
                      </div>
                    </div>
                  </div>
                )
              })}

            </div>

            <ListPagination
              currentPage={currentPage}
              totalPages={totalPages}
              loading={loading}
              onNext={() => setCurrentPage((page) => page + 1)}
            />
          </>
        )}
      </div>
    </DashboardLayout>
  )
}

export default VehicleContractList
